import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import "./UserPanelPages.css";
import Navbar from "../Component/Common/Navbar";
import {
  connectRealtimeSocket,
  disconnectRealtimeSocket,
} from "../Component/ServerPackage/CompleteCPanel/utils/realtimeSocket";

/* ==============================
   API BASE HELPER
============================== */
const normalizeBase = (value) => String(value || "").trim().replace(/\/+$/, "");

const ensureApiBase = (value) => {
  const clean = normalizeBase(value);

  if (!clean) return "http://localhost:5000/api";

  return clean.endsWith("/api") ? clean : `${clean}/api`;
};

const API_BASE = ensureApiBase(
  process.env.REACT_APP_API_BASE || "http://localhost:5000"
);

/* ==============================
   AUTH KEYS
============================== */
const AUTH_TOKEN_KEYS = [
  "token",
  "authToken",
  "accessToken",
  "userToken",
  "jwtToken",
];

const AUTH_STORAGE_KEYS = [
  "token",
  "authToken",
  "accessToken",
  "userToken",
  "jwtToken",
  "isLoggedIn",
  "user",
  "currentUser",
  "email",
  "userEmail",
  "user_email",
  "fullName",
  "name",
  "userName",
  "username",
];

/* ==============================
   AUTH HELPERS
============================== */
const getToken = () => {
  for (const key of AUTH_TOKEN_KEYS) {
    const localValue = localStorage.getItem(key);
    const sessionValue = sessionStorage.getItem(key);

    if (localValue && String(localValue).trim()) {
      return String(localValue).trim();
    }

    if (sessionValue && String(sessionValue).trim()) {
      return String(sessionValue).trim();
    }
  }

  return "";
};

const clearAuth = () => {
  AUTH_STORAGE_KEYS.forEach((key) => {
    localStorage.removeItem(key);
    sessionStorage.removeItem(key);
  });

  disconnectRealtimeSocket();
  window.dispatchEvent(new Event("auth-state-changed"));
};

/* ==============================
   FORMAT HELPERS
============================== */
const formatInr = (amount = 0) => {
  return `₹${Number(amount || 0).toLocaleString("en-IN")}`;
};

const formatDateTime = (value) => {
  if (!value) return "-";

  try {
    return new Date(value).toLocaleString("en-IN", {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return "-";
  }
};

const getWalletStatus = (status) => {
  return String(status || "Active").trim();
};

function WalletPage() {
  const navigate = useNavigate();

  const [wallet, setWallet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [realtimeStatus, setRealtimeStatus] = useState("connecting");
  const [status, setStatus] = useState({ type: "", message: "" });

  /* ==============================
     PROTECTED API FETCH
  ============================== */
  const authFetch = useCallback(async (endpoint, options = {}) => {
    const token = getToken();

    if (!token) {
      clearAuth();
      throw new Error("Session expired. Please login again.");
    }

    const response = await fetch(`${API_BASE}${endpoint}`, {
      ...options,
      cache: "no-store",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
        ...(options.headers || {}),
      },
    });

    const data = await response.json().catch(() => ({}));

    if (response.status === 401 || response.status === 403) {
      clearAuth();
      throw new Error("Session expired. Please login again.");
    }

    if (!response.ok || data?.success === false) {
      throw new Error(data?.message || "Request failed.");
    }

    return data;
  }, []);

  /* ==============================
     LOAD WALLET
  ============================== */
  const fetchWallet = useCallback(
    async (silent = false) => {
      try {
        if (!silent) {
          setLoading(true);
        } else {
          setRefreshing(true);
        }

        setStatus({ type: "", message: "" });

        const data = await authFetch("/wallet/my-wallet");

        setWallet(data?.data || null);
      } catch (error) {
        setWallet(null);

        setStatus({
          type: "error",
          message: error.message || "Failed to fetch wallet.",
        });

        if (
          String(error.message || "")
            .toLowerCase()
            .includes("session expired")
        ) {
          setTimeout(() => {
            navigate("/signin", { replace: true });
          }, 700);
        }
      } finally {
        if (!silent) {
          setLoading(false);
        } else {
          setRefreshing(false);
        }
      }
    },
    [authFetch, navigate]
  );

  useEffect(() => {
    fetchWallet();
  }, [fetchWallet]);

  /* ==============================
     REALTIME SOCKET
  ============================== */
  useEffect(() => {
    const socket = connectRealtimeSocket();

    if (!socket) {
      setRealtimeStatus("offline");
      return undefined;
    }

    let refreshTimer = null;

    const softRefresh = () => {
      clearTimeout(refreshTimer);

      refreshTimer = setTimeout(() => {
        fetchWallet(true);
      }, 400);
    };

    const handleConnected = () => {
      setRealtimeStatus("connected");
    };

    const handleDisconnected = () => {
      setRealtimeStatus("offline");
    };

    const handleWalletUpdate = (payload = {}) => {
      setRealtimeStatus("connected");

      if (payload?.wallet) {
        setWallet(payload.wallet);
      }

      setStatus({
        type: "success",
        message: payload?.message || "Wallet updated successfully.",
      });

      softRefresh();
    };

    const handlePaymentStatus = (payload = {}) => {
      setRealtimeStatus("connected");

      if (payload?.status === "success") {
        setStatus({
          type: "success",
          message: payload.message || "Payment completed successfully.",
        });
      }

      softRefresh();
    };

    const handleRealtimeRefresh = () => {
      setRealtimeStatus("connected");
      softRefresh();
    };

    const handleSocketAuthError = () => {
      clearAuth();
      navigate("/signin", { replace: true });
    };

    socket.on("connect", handleConnected);
    socket.on("disconnect", handleDisconnected);
    socket.on("socket:connected", handleConnected);

    socket.on("wallet:update", handleWalletUpdate);
    socket.on("wallet:status", handleWalletUpdate);

    socket.on("payment:status", handlePaymentStatus);
    socket.on("dashboard:update", handleRealtimeRefresh);
    socket.on("notification:new", handleRealtimeRefresh);
    socket.on("activity:new", handleRealtimeRefresh);
    socket.on("domain:status", handleRealtimeRefresh);
    socket.on("hosting:status", handleRealtimeRefresh);

    socket.on("connect_error", handleSocketAuthError);

    if (socket.connected) {
      handleConnected();
    }

    return () => {
      clearTimeout(refreshTimer);

      socket.off("connect", handleConnected);
      socket.off("disconnect", handleDisconnected);
      socket.off("socket:connected", handleConnected);

      socket.off("wallet:update", handleWalletUpdate);
      socket.off("wallet:status", handleWalletUpdate);

      socket.off("payment:status", handlePaymentStatus);
      socket.off("dashboard:update", handleRealtimeRefresh);
      socket.off("notification:new", handleRealtimeRefresh);
      socket.off("activity:new", handleRealtimeRefresh);
      socket.off("domain:status", handleRealtimeRefresh);
      socket.off("hosting:status", handleRealtimeRefresh);

      socket.off("connect_error", handleSocketAuthError);

      disconnectRealtimeSocket();
    };
  }, [fetchWallet, navigate]);

  const handleRefresh = () => {
    fetchWallet(true);
  };

  const walletBalance = Number(wallet?.balance || 0);
  const walletStatus = getWalletStatus(wallet?.status);

  return (
    <>
      <Navbar />

      <section className="user-page-section">
        <div className="user-page-container">
          <div className="user-page-head">
            <h1>Wallet / Credits</h1>
            <p>Your available balance and wallet details.</p>

            <div className="wallet-realtime-row">
              <div className="wallet-realtime-chip">
                <span
                  className={
                    realtimeStatus === "connected"
                      ? "wallet-realtime-dot online"
                      : "wallet-realtime-dot offline"
                  }
                />

                <span>
                  {realtimeStatus === "connected"
                    ? "Realtime connected"
                    : realtimeStatus === "connecting"
                    ? "Connecting realtime..."
                    : "Realtime offline"}
                </span>
              </div>

              <button
                type="button"
                className="wallet-refresh-btn"
                onClick={handleRefresh}
                disabled={loading || refreshing}
              >
                {refreshing ? "Refreshing..." : "Refresh"}
              </button>
            </div>
          </div>

          {status.message ? (
            <div className={`user-page-alert ${status.type}`}>
              {status.message}
            </div>
          ) : null}

          <div className="user-page-card">
            {loading ? (
              <p className="user-page-muted">Loading wallet...</p>
            ) : !wallet ? (
              <p className="user-page-muted">No wallet data found yet.</p>
            ) : (
              <>
                <div className="wallet-balance-hero">
                  <span>Available Balance</span>
                  <strong>{formatInr(walletBalance)}</strong>
                  <p>
                    Wallet balance is linked securely to your logged-in account.
                  </p>
                </div>

                <div className="profile-grid">
                  <div className="profile-box">
                    <strong>Current Balance</strong>
                    <span>{formatInr(walletBalance)}</span>
                  </div>

                  <div className="profile-box">
                    <strong>Wallet Status</strong>
                    <span>{walletStatus}</span>
                  </div>

                  <div className="profile-box">
                    <strong>Last Updated</strong>
                    <span>{formatDateTime(wallet.updated_at)}</span>
                  </div>

                  <div className="profile-box">
                    <strong>Wallet ID</strong>
                    <span>{wallet.id ? `WAL-${wallet.id}` : "-"}</span>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </section>
    </>
  );
}

export default WalletPage;